import React from 'react';
import { Box, Tooltip, Checkbox } from '@mui/material';

/**
 * Cell styles for different states
 */
export const CELL_STYLES = {
  updated: {
    backgroundColor: 'rgba(255, 165, 0, 0.2)',
  },
  newRow: {
    backgroundColor: 'rgba(255, 200, 100, 0.3)',
  },
  positive: {
    backgroundColor: 'rgba(76, 175, 80, 0.2)',
  },
  negative: {
    backgroundColor: 'rgba(244, 67, 54, 0.2)',
  },
  highChange: {
    backgroundColor: 'rgba(221, 160, 221, 0.5)',
  },
  monthDiff: {
    backgroundColor: 'rgba(255, 0, 0, 0.2)',
    border: '1px solid rgba(255, 0, 0, 0.4)',
  },
  readonly: {
    backgroundColor: '#f5f5f5',
    fontStyle: 'italic',
  },
};

/**
 * Creates a cell class getter function for change tracking
 *
 * @param {Object} options
 * @param {Object|Map} options.updatedRows - Map of row ID to changed fields
 * @param {Function} options.getRowId - Function to get row ID from data
 * @param {Function} [options.isNewRow] - Function to check if row is new
 * @returns {Function} Cell class getter function
 *
 * @example
 * const getCellClass = createCellClassGetter({
 *   updatedRows,
 *   getRowId: (data) => data.BLOTTER_ID,
 *   isNewRow: (data) => data.BLOTTER_ID.startsWith('new_row_'),
 * });
 */
export const createCellClassGetter = ({
  updatedRows,
  getRowId,
  isNewRow,
}) => {
  return (params) => {
    const { data, colDef } = params;
    const field = colDef.field;

    // Skip non-data columns
    if (!data || !field || ['actions', 'select', 'edit'].includes(field)) {
      return '';
    }

    const rowId = getRowId(data);
    const rowIsNew = isNewRow ? isNewRow(data) : false;
    const isUpdated = updatedRows?.[rowId]?.[field];

    if (rowIsNew) {
      return 'ag-cell-updated-new-row';
    } else if (isUpdated) {
      return 'ag-cell-updated';
    }

    return '';
  };
};

/**
 * Creates a cell style getter for change highlighting
 *
 * @param {Object} options
 * @param {Object|Map} options.updatedRows - Map of row ID to changed fields
 * @param {Function} options.getRowId - Function to get row ID from data
 * @param {Function} [options.isNewRow] - Function to check if row is new
 * @param {Object} [options.baseStyle={}] - Base cell style
 * @returns {Function} Cell style getter function
 */
export const createCellStyleGetter = ({
  updatedRows,
  getRowId,
  isNewRow,
  baseStyle = {},
}) => {
  return (params) => {
    const { data, colDef } = params;
    const field = colDef.field;

    // Skip non-data columns
    if (!data || !field || ['actions', 'select', 'edit'].includes(field)) {
      return baseStyle;
    }

    const rowId = getRowId(data);
    const rowIsNew = isNewRow ? isNewRow(data) : false;
    const isUpdated = updatedRows?.[rowId]?.[field];

    if (rowIsNew) {
      return { ...baseStyle, ...CELL_STYLES.newRow };
    } else if (isUpdated) {
      return { ...baseStyle, ...CELL_STYLES.updated };
    }

    return baseStyle;
  };
};

/**
 * Percent change cell renderer with color coding
 */
export const PercentChangeRenderer = (params) => {
  const value = params.value;

  if (value === null || value === undefined || value === '') {
    return <span style={CELL_STYLES.readonly}>-</span>;
  }

  const numValue = parseFloat(value);
  const isPositive = numValue > 0;
  const isNegative = numValue < 0;
  const sign = isPositive ? '+' : '';

  const style = {
    ...(isPositive ? CELL_STYLES.positive : isNegative ? CELL_STYLES.negative : {}),
    padding: '2px 4px',
    borderRadius: '2px',
  };

  return (
    <span style={style}>
      {sign}{numValue.toFixed(2)}%
    </span>
  );
};

/**
 * Month-over-month change renderer with highlighting
 */
export const MonthChangeRenderer = ({
  currentValue,
  previousValue,
  threshold = 0,
  formatValue,
}) => {
  const hasChanged = currentValue !== previousValue;
  const exceedsThreshold = threshold > 0 &&
    Math.abs(currentValue - previousValue) >= threshold;

  const displayValue = formatValue ? formatValue(currentValue) : currentValue;

  const style = {
    ...(exceedsThreshold ? CELL_STYLES.monthDiff : hasChanged ? CELL_STYLES.updated : {}),
    width: '100%',
    display: 'block',
  };

  if (hasChanged && previousValue !== undefined) {
    return (
      <Tooltip title={`Previous: ${formatValue ? formatValue(previousValue) : previousValue}`}>
        <span style={style}>{displayValue}</span>
      </Tooltip>
    );
  }

  return <span style={style}>{displayValue}</span>;
};

/**
 * Creates a month comparison cell style getter
 *
 * @param {Object} options
 * @param {Function} options.getPreviousValue - Function to get previous month value (cusip, field) => value
 * @param {Object} [options.thresholds] - Field-specific thresholds for highlighting
 * @returns {Function} Cell style getter
 */
export const createMonthComparisonStyle = ({
  getPreviousValue,
  thresholds = {},
}) => {
  return (params) => {
    const { data, colDef, value } = params;
    const field = colDef.field;

    if (!data || value === undefined || value === null || value === '') {
      return {};
    }

    const prevValue = getPreviousValue(data.CUSIP, field);
    if (prevValue === undefined || prevValue === null) {
      return {};
    }

    const threshold = thresholds[field] || 0;
    const diff = Math.abs(parseFloat(value) - parseFloat(prevValue));

    if (threshold > 0 && diff >= threshold) {
      return CELL_STYLES.monthDiff;
    }

    if (value !== prevValue) {
      return CELL_STYLES.updated;
    }

    return {};
  };
};

/**
 * Currency formatter cell renderer
 */
export const CurrencyRenderer = (params) => {
  const value = params.value;

  if (value === null || value === undefined || value === '') {
    return '';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: params.currency || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Number formatter with configurable decimals
 */
export const createNumberFormatter = (decimals = 2) => {
  return (params) => {
    const value = params.value;

    if (value === null || value === undefined || value === '') {
      return '';
    }

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };
};

/**
 * Percentage formatter
 */
export const PercentageRenderer = (params) => {
  const value = params.value;

  if (value === null || value === undefined || value === '') {
    return '';
  }

  // Assumes value is already in percentage form (e.g., 4.5 for 4.5%)
  return `${parseFloat(value).toFixed(3)}%`;
};

/**
 * Date formatter for DATE-ONLY (calendar date) columns.
 *
 * Renders in the UTC frame, NOT the browser-local zone: a date-only value arrives
 * as 'YYYY-MM-DD' (or midnight UTC), and `new Date('2026-05-31').toLocaleDateString()`
 * would render the PREVIOUS day for clients west of UTC. Pinning the formatter to
 * timeZone:'UTC' keeps the same en-US format while preventing the day shift. Use
 * DateTimeRenderer (local) only for true timestamps/instants.
 */
export const DateRenderer = (params) => {
  const value = params.value;

  if (!value) return '';

  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC' }).format(date);
  } catch {
    return value;
  }
};

/**
 * DateTime formatter
 */
export const DateTimeRenderer = (params) => {
  const value = params.value;

  if (!value) return '';

  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleString('en-US');
  } catch {
    return value;
  }
};

/**
 * Boolean Y/N renderer
 */
export const BooleanRenderer = (params) => {
  const value = params.value;

  if (value === true || value === 'Y' || value === 'Yes' || value === '1') {
    return 'Y';
  }
  if (value === false || value === 'N' || value === 'No' || value === '0') {
    return 'N';
  }
  return '';
};

/**
 * Truncated text with tooltip
 */
export const TruncatedTextRenderer = (params) => {
  const value = params.value;
  const maxLength = params.maxLength || 50;

  if (!value) return '';

  const text = String(value);
  if (text.length <= maxLength) {
    return text;
  }

  return (
    <Tooltip title={text}>
      <span>{text.substring(0, maxLength)}...</span>
    </Tooltip>
  );
};

/**
 * Selection checkbox cell renderer for row selection
 *
 * Uses grid context for dynamic selectedRows state (context.selectionState)
 * Falls back to cellRendererParams for static configuration
 *
 * @param {Object} props - Cell renderer params from AG Grid plus custom props
 * @param {Object} props.data - Row data
 * @param {Object} props.context - Grid context with selectionState
 * @param {boolean} props.disabled - Whether checkbox is disabled
 */
export const SelectionCheckboxRenderer = (props) => {
  const { data, context, getRowId: paramGetRowId, disabled: paramDisabled } = props;

  // Get selection state from context (dynamic) or fall back to params (static)
  const selectionState = context?.selectionState || {};
  const selectedRows = selectionState.selectedRows || props.selectedRows;
  const onRowSelectionChange = selectionState.onRowSelectionChange || props.onRowSelectionChange;
  const getRowId = selectionState.getRowId || paramGetRowId;
  const disabled = selectionState.disabled ?? paramDisabled;

  if (!data || !getRowId) return null;

  const rowId = getRowId(data);
  const isSelected = selectedRows?.has?.(rowId) || false;

  const handleChange = (event) => {
    event.stopPropagation();
    if (onRowSelectionChange && !disabled) {
      const newSelectedRows = new Set(selectedRows);
      if (event.target.checked) {
        newSelectedRows.add(rowId);
      } else {
        newSelectedRows.delete(rowId);
      }
      onRowSelectionChange(newSelectedRows);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
      <Checkbox
        checked={isSelected}
        onChange={handleChange}
        disabled={disabled}
        size="small"
        sx={{ padding: 0 }}
      />
    </Box>
  );
};

/**
 * Select All header component for selection column
 * Selects/deselects all visible (filtered) rows
 *
 * Uses grid context for dynamic selectedRows state (context.selectionState)
 * Falls back to headerComponentParams for static configuration
 *
 * @param {Object} props - Header component params from AG Grid
 * @param {Object} props.api - AG Grid API
 * @param {Object} props.context - Grid context with selectionState
 * @param {Function} props.getRowId - Function to get row ID from data
 * @param {boolean} props.disabled - Whether checkbox is disabled
 */
export const SelectAllHeaderComponent = (props) => {
  const { api, context, getRowId: paramGetRowId, disabled: paramDisabled } = props;

  // Get selection state from context (dynamic) or fall back to params (static)
  const selectionState = context?.selectionState || {};
  const selectedRows = selectionState.selectedRows || props.selectedRows;
  const onRowSelectionChange = selectionState.onRowSelectionChange || props.onRowSelectionChange;
  const getRowId = selectionState.getRowId || paramGetRowId;
  const disabled = selectionState.disabled ?? paramDisabled;

  // Get visible (filtered) row IDs
  const getVisibleRowIds = () => {
    const visibleRowIds = [];
    if (api) {
      api.forEachNodeAfterFilter((node) => {
        if (node.data && getRowId) {
          visibleRowIds.push(getRowId(node.data));
        }
      });
    }
    return visibleRowIds;
  };

  const visibleRowIds = getVisibleRowIds();
  const selectedSet = selectedRows || new Set();

  const isAllSelected = visibleRowIds.length > 0 &&
    visibleRowIds.every(id => selectedSet.has(id));
  const isPartiallySelected = visibleRowIds.some(id => selectedSet.has(id)) && !isAllSelected;

  const handleHeaderClick = (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (disabled || !onRowSelectionChange) return;

    const currentVisibleRowIds = getVisibleRowIds();
    if (isAllSelected) {
      // Deselect all visible rows
      const newSelectedRows = new Set(selectedSet);
      currentVisibleRowIds.forEach(id => newSelectedRows.delete(id));
      onRowSelectionChange(newSelectedRows);
    } else {
      // Select all visible rows
      const newSelectedRows = new Set(selectedSet);
      currentVisibleRowIds.forEach(id => newSelectedRows.add(id));
      onRowSelectionChange(newSelectedRows);
    }
  };

  return (
    <Box
      onClick={handleHeaderClick}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        width: '100%',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      <Checkbox
        checked={isAllSelected}
        indeterminate={isPartiallySelected}
        disabled={disabled}
        size="small"
        sx={{ padding: 0, pointerEvents: 'none' }}
      />
    </Box>
  );
};

/**
 * Creates a selection column definition for PamGrid
 *
 * @param {Object} options
 * @param {Set} options.selectedRows - Set of selected row IDs
 * @param {Function} options.getRowId - Function to get row ID from data
 * @param {Function} options.onRowSelectionChange - Callback when selection changes
 * @param {number} [options.width=50] - Column width
 * @param {boolean} [options.disabled=false] - Whether selection is disabled
 * @returns {Object} AG Grid column definition
 */
export const createSelectionColumn = ({
  selectedRows,
  getRowId,
  onRowSelectionChange,
  width = 50,
  disabled = false,
}) => {
  return {
    headerName: '',
    field: 'select',
    width,
    minWidth: width,
    maxWidth: width,
    pinned: 'left',
    sortable: false,
    filter: false,
    editable: false,
    suppressHeaderMenuButton: true,
    lockPosition: true,
    cellRenderer: SelectionCheckboxRenderer,
    cellRendererParams: {
      selectedRows,
      getRowId,
      onRowSelectionChange,
      disabled,
    },
    headerComponent: SelectAllHeaderComponent,
    headerComponentParams: {
      selectedRows,
      getRowId,
      onRowSelectionChange,
      disabled,
    },
  };
};

const cellRenderers = {
  CELL_STYLES,
  createCellClassGetter,
  createCellStyleGetter,
  PercentChangeRenderer,
  MonthChangeRenderer,
  createMonthComparisonStyle,
  CurrencyRenderer,
  createNumberFormatter,
  PercentageRenderer,
  DateRenderer,
  DateTimeRenderer,
  BooleanRenderer,
  TruncatedTextRenderer,
  SelectionCheckboxRenderer,
  SelectAllHeaderComponent,
  createSelectionColumn,
};

export default cellRenderers;
